import dayjs from 'dayjs';
import 'dayjs/locale/da';
import {ComponentSubmitHandler, DefaultTimelineMetadata, ProductInstance, FlowData} from './types';
import now from "./now.ts";

export const MAX_DATE = '9999-12-31';

export function getCurrentDateTime(): string {
    return dayjs().format('YYYY-MM-DD HH:mm:ss');
}

export function isDateSame(date: string): boolean {
    return now.isSame(date, 'day');
}

export function isDateAfter(date: string): boolean {
    return now.isAfter(date, 'day');
}

export function isDateSameOrAfter(date: string): boolean {
    return isDateSame(date) || isDateAfter(date);
}

export function isDateBefore(date: string): boolean {
    return now.isBefore(date, 'day');
}

export function isPastDays(date: string, days: number): boolean {
    return now.isAfter(dayjs(date).add(days, 'day'));
}

export function isPastMinutes(date: string, minutes: number): boolean {
    return dayjs().isAfter(dayjs(date).add(minutes, 'minute'));
}

export function isDateMaxDate(date: string): boolean {
    return dayjs(date).isSame(dayjs(MAX_DATE));
}

export function dateCompare(date1: string, date2: string): number {
    if (date1 === date2) {
        return 0;
    }
    if (date1 < date2) {
        return -1;
    }

    return 1;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function findMetadata(key: string, metadata: DefaultTimelineMetadata[]): any {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return metadata.find((metadata: any) => metadata.key === key)?.value;
}

export const shouldShowWinterHolidayWarning = () => {
    const mondayDay = 0
    const januaryMonth = 0
    const decemberMonth = 11
    return (now.month() === decemberMonth || (now.month() === januaryMonth && now.day() === mondayDay))
}

export const shouldShowConstitutionDayWarning = () => {
    const constitutionDay = 5
    const juneMonth = 5
    return now.month() === juneMonth && now.date() <= constitutionDay
}

export function validateEmail(email: string): boolean {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    return regex.test(email);
}

export function validatePhone(phone: string): boolean {
    const normalized = phone.replace(/\s+/g, '');
    const regex = /^(?:0045\d{8}|\+45\d{8}|\d{8})$/;

    return regex.test(normalized) && normalized.length === 12;
}

const components: Map<string,ComponentSubmitHandler> = new Map();
export function registerComponentsWithValidation(component_name: string, component: ComponentSubmitHandler): void {
    if (!component_name || !component) {
        throw new Error(`Invalid component_name or component: ${component_name}, ${component}`);
    }

    if (components.has(component_name)) {
        console.warn(`Component with name "${component_name}" is already registered.`);
        return;
    }

    // Optional: Validate that the component matches the expected interface
    if (typeof component.hasErrors !== 'function' || typeof component.scrollToError !== 'function') {
        console.error('Attempting to register an invalid component.');
        return;
    }

    components.set(component_name, component);
}

export function anyComponentsWithValidationError(flowData: FlowData): boolean {
    for (const component of components.values()) {
        if (component.hasErrors(flowData)){
            component.scrollToError();
            return true;
        }
    }
    return false;
}

export function scrollToElement(element: HTMLDivElement) {
    element.scrollIntoView(true)

    let siteHeaderOffset: number = 75 // default height on my screen, but is better than 0
    let selfcareHeaderOffset: number = 70 // default height on my screen, but is better than 0

    const site_header_element= document.getElementById('__site-header')
    if (site_header_element != null){
        siteHeaderOffset = site_header_element.offsetHeight
    }

    const selfcare_header_wrapper = document.getElementById('__selfcare-header-wrapper')
    if (selfcare_header_wrapper != null ){
        selfcareHeaderOffset = selfcare_header_wrapper.offsetHeight
    }

    window.scrollBy( 0, -(siteHeaderOffset+selfcareHeaderOffset+10))
}

export function stripPhonePrefix(phone: string): string {
    if (phone.startsWith('0045')) {
        return phone.slice(4);
    } else if (phone.startsWith('+45')) {
        return phone.slice(3);
    }
    return phone;
}

export const filterOnProductsThatCanSeeTheNetwork = (product_instances: ProductInstance[] ) => {
    return product_instances.filter((product_instance) => (product_instance.can_see_network_overview))
}

export const filterIllegalCharcters = (input: string) => input.replace(/[^a-zA-Z0-9æøåÆØÅ\s.,!?'"()/-]/g, '');

export const filterGponSerialIllegalCharcters = (input: string) => input.replace(/[^a-zA-Z0-9-\s]/g, '');
